import React from 'react';
import Link from 'gatsby-link';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Data
import RDA from '../../../data/rda.json';

// Config
import { nutrientsThresholds } from '../../config';

// Utils
import {
  formatVitaminName,
  getAltVitaminName,
  getShortNutrientName,
  iuToMcg
} from '../../utils';

// Styles
import {
  Wrapper,
  AbsentNutrientsWrapper,
  NutrientListItem,
  NutrientsNav,
  StatsSection,
  BarWrapper
} from './NutrientStatsStyles';

// todo - move to config
const nutrientBlacklist = [
  'Water',
  'Vitamin D (D2 + D3)',
  'Vitamin A, IU',
  'Fatty acids, total monounsaturated',
  'Fatty acids, total polyunsaturated',
  'Total lipid (fat)'
];

const isValidNutrient = nutrientName => nutrientBlacklist.indexOf(nutrientName) === -1;

const Bar = ({ val }) => (
  <BarWrapper>
    <div style={{ width: `${Math.min(val, 100)}%` }}><span>{val > 0 ? val.toFixed(2) : 0}</span></div>
  </BarWrapper>
);

const getBarViz = perc => perc > 0 ? <Bar val={perc}></Bar> : '';

const getRDABar = (value, units, nutrientName, rdaKey = 'f') => {

  // we may be using a shorter key in the RDA config
  const key = nutrientName in RDA ? nutrientName : getShortNutrientName(nutrientName);
  const rda = RDA[key];
  if (rda.units === units) {
    const perc = 100 * value / rda[rdaKey];
    return getBarViz(perc);
  } else {
    // different units
    if (units === 'IU' && rda.units === 'µg') {
      const perc = 100 * iuToMcg(value, nutrientName) / rda[rdaKey];
      return getBarViz(perc);
    } else {
      console.log('No RDA conversion possible', key, units, rda.units)
      return '?';
    }
  }
};

const hasRDAMatch = nutrientName => {
  return getShortNutrientName(nutrientName) in RDA;
};

class NutrientStats extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeKey: props.keys[0]
    };

    this._onKeyTapped = this._onKeyTapped.bind(this);
  }
  _onKeyTapped(e) {
    const index = parseInt(e.target.getAttribute('data-index'));
    this.setState({
      activeKey: this.props.keys[index]
    });
  }
  render() {
    const { allNutrients, keys, data, serving } = this.props;
    const { activeKey } = this.state;

    const activeNutrients = data.filter(n => {
      return isValidNutrient(n.name) && n.value > 0;
    });
    const inactiveNutrients = data.filter(n => {
      return isValidNutrient(n.name) && Math.abs(n.value) === 0;
    });

    // console.table(activeNutrients);
    // console.log('nutrientsThresholds', nutrientsThresholds, activeNutrients);

    const groupedData = {
      tiny: [],
      some: [],
      good: [],
      lots: [],
      other: []
    };

    activeNutrients.forEach(n => {
      // const valuePerServing = n.value * (serving / 100);
      const rdaKey = activeKey.key;
      const nutrientName = formatVitaminName(n.name);
      const key = nutrientName in RDA ? nutrientName : getShortNutrientName(nutrientName);
      const rda = RDA[key];
      const valuePer100g = n.value;
      const valuePerServing = valuePer100g * (serving / 100);
      const units = n.unit;
      let perc = 0;

      if (rda && rda.units === units) {
        perc = 100 * valuePerServing / rda[rdaKey];
      } else {
        // different units
        if (units === 'IU' && rda.units === 'µg') {
          perc = 100 * iuToMcg(value, nutrientName) / rda[rdaKey];
        } else {
          console.log('no rda conversion possible', nutrientName, key);
        }
      }

      const pathData = _.find(allNutrients, nutrient => nutrient.name.toLowerCase() === key.toLowerCase()) || '';

      if (perc < nutrientsThresholds.some) {
        groupedData.tiny.push({ ...n, pathData });
      } else if (perc < nutrientsThresholds.good) {
        groupedData.some.push({ ...n, pathData });
      } else if (perc < nutrientsThresholds.lots) {
        groupedData.good.push({ ...n, pathData });
      } else if (perc >= nutrientsThresholds.lots) {
        groupedData.lots.push({ ...n, pathData });
      } else {
        groupedData.other.push({ ...n, pathData });
      }
    });

    const createNutrientsList = nutrientsData => (
      <ul>
        {nutrientsData.filter(n => isValidNutrient(n.name)).map((n, i) => {
          const nutrientName = formatVitaminName(n.name);
          const valuePer100g = n.value;
          const units = n.unit;
          const path = n.pathData.path || '';
          const valuePerServing = valuePer100g * (serving / 100);

          return (
            <NutrientListItem key={n.nutrient_id} serving={serving}>
              <span>{
                path
                  ? <Link to={path}>{getShortNutrientName(nutrientName)}</Link>
                  : getShortNutrientName(nutrientName)
              }</span>
              <span data-value={valuePerServing.toFixed(2) + n.unit} >{hasRDAMatch(nutrientName) ? getRDABar(valuePerServing, n.unit, nutrientName, activeKey.key) : <BarWrapper><div></div></BarWrapper>}</span>
            </NutrientListItem>
          );
        })}
      </ul>
    );

    return (
      <Wrapper>
        <NutrientsNav>
          {keys.map((k, i) => <a title={`${k.label} (Recommended Daily Allowance)`} data-active={activeKey.key === k.key} key={k.key} onClick={this._onKeyTapped} data-index={i}>{k.label}</a>)}
        </NutrientsNav>
        {groupedData.lots.length
          ? (
          <StatsSection>
            <h4>Loads of...</h4>
            {createNutrientsList(groupedData.lots)}
          </StatsSection>)
          : ''
        }
          {groupedData.good.length
            ? (
            <StatsSection>
              <h4>A good amount of...</h4>
              {createNutrientsList(groupedData.good)}
            </StatsSection>
          ) : ''
        }
        {groupedData.some.length
          ? (
          <StatsSection>
            <h4>A small amount of...</h4>
            {createNutrientsList(groupedData.some)}
          </StatsSection>
          ) : ''
        }
        {groupedData.tiny.length
          ? (
          <StatsSection>
            <h4>A tiny amount of</h4>
            {createNutrientsList(groupedData.tiny)}
          </StatsSection>
          ) : ''
        }
        <AbsentNutrientsWrapper>
          <p>Absent Nutrients: {inactiveNutrients.map(n => formatVitaminName(getShortNutrientName(n.name))).join(', ')}</p>
          <p>Recommended Daily Allowance (RDA) % shown for adult {activeKey.label.toLowerCase()}, as published by the <a href='https://www.nhs.uk/conditions/vitamins-and-minerals/' target='_blank'>NHS (UK)</a>.</p>
        </AbsentNutrientsWrapper>
      </Wrapper>
    );
  }
}

NutrientStats.propTypes = {
  allNutrients: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired
  })).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  keys: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired
  })).isRequired,
  serving: PropTypes.number.isRequired
};

export default NutrientStats;



// WEBPACK FOOTER //
// ./src/components/NutrientStats/NutrientStats.jsx